<!-- ER图显示组件 - SVG版本 -->
<template>
  <div class="diagram-output">
    <div class="toolbar">
      <div class="toolbar-left">
        <div class="canvas-hint-toolbar">💡 提示：节点可拖拽 · 画布可缩放 · 双击文本可编辑</div>
      </div>
      <div class="toolbar-right">
        <button class="tool-btn btn-purple" @click="openEntitySelectDialog">
          <i class="el-icon-s-operation"></i>筛选实体
        </button>
        <button class="tool-btn btn-orange" @click="toggleFullscreen">
          <i class="el-icon-full-screen"></i>全屏展示
        </button>
        <button class="tool-btn btn-blue" @click="openExportDialog">
          <i class="el-icon-download"></i>导出ER图
        </button>
      </div>
    </div>

    <!-- 空状态提示 -->
    <div v-if="entities.length === 0" class="empty-state">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="64" height="64">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
      </svg>
      <p class="empty-title">暂无ER图数据</p>
      <p class="empty-description">请在左侧输入SQL建表语句，然后点击"总体 ER 图"按钮生成</p>
    </div>

    <!-- 图表视图 -->
    <div v-else class="diagram-wrapper">
      <div class="diagram-container" ref="diagramContainer">
        <svg 
          class="diagram-svg"
          :viewBox="zoomedViewBox"
          preserveAspectRatio="xMidYMid meet"
          @mousedown="startPan"
          @mousemove="onDrag"
          @mouseup="endDrag"
          @mouseleave="endDrag"
          @wheel.prevent="handleWheel"
          :class="{ 'panning': isPanning }">
          
          <!-- 连接线（属性到实体） - 最底层 -->
          <g class="connections">
            <line
              v-for="link in links"
              :key="link.id"
              v-show="isLinkVisible(link)"
              :x1="link.x1"
              :y1="link.y1"
              :x2="link.x2"
              :y2="link.y2"
              stroke="#000000"
              stroke-width="1"
              fill="none">
            </line>
          </g>

          <!-- 外键关系的连接线 - 第二层 -->
          <g class="foreign-key-lines">
            <g v-for="relation in foreignKeyRelations" :key="'lines-' + relation.id" v-show="relation.visible">
              <!-- 从源实体到菱形的线 -->
              <line
                :x1="relation.sourceX"
                :y1="relation.sourceY"
                :x2="relation.diamondSourceX"
                :y2="relation.diamondSourceY"
                stroke="#000000"
                stroke-width="1"
                fill="none">
              </line>
              
              <!-- 从菱形到目标实体的线 -->
              <line
                :x1="relation.diamondTargetX"
                :y1="relation.diamondTargetY"
                :x2="relation.targetX"
                :y2="relation.targetY"
                stroke="#000000"
                stroke-width="1"
                fill="none">
              </line>
            </g>
          </g>

          <!-- 实体节点（矩形） -->
          <g class="entities">
            <g v-for="entity in entities" :key="entity.key" v-show="entity.visible">
              <rect
                :x="entity.x"
                :y="entity.y"
                :width="entity.width"
                :height="entity.height"
                stroke="#000000"
                stroke-width="1"
                fill="white"
                @mousedown.stop="startDragNode($event, entity)"
                @dblclick.stop="startEditText(entity)"
                class="draggable-node">
              </rect>
              <text
                v-if="editingNode !== entity"
                :x="entity.x + entity.width / 2"
                :y="entity.y + entity.height / 2"
                text-anchor="middle"
                dominant-baseline="middle"
                font-family="宋体"
                font-size="18"
                fill="#000000"
                @mousedown.stop="startDragNode($event, entity)"
                @dblclick.stop="startEditText(entity)"
                pointer-events="none">
                {{ entity.name }}
              </text>
              <foreignObject
                v-if="editingNode === entity"
                :x="entity.x"
                :y="entity.y"
                :width="entity.width"
                :height="entity.height">
                <input
                  type="text"
                  v-model="editingText"
                  @blur="finishEdit"
                  @keyup.enter="finishEdit"
                  @keyup.esc="cancelEdit"
                  ref="editInput"
                  class="edit-input"
                  style="width: 100%; height: 100%; text-align: center; font-size: 18px; font-family: 宋体; border: 2px solid #409eff; outline: none;">
              </foreignObject>
              <!-- 调整大小手柄 -->
              <circle
                v-if="selectedNode === entity"
                :cx="entity.x + entity.width"
                :cy="entity.y + entity.height"
                r="6"
                fill="#409eff"
                stroke="white"
                stroke-width="2"
                @mousedown.stop="startResize($event, entity)"
                class="resize-handle">
              </circle>
            </g>
          </g>

          <!-- 外键关系的菱形和文字 -->
          <g class="foreign-key-relations">
            <g v-for="relation in foreignKeyRelations" :key="relation.id" v-show="relation.visible">
              <!-- 菱形（关系） -->
              <polygon
                :points="relation.diamondPoints"
                stroke="#000000"
                stroke-width="1"
                fill="white"
                class="relationship-diamond"
                @mousedown.stop="startDragRelation($event, relation)"
                @dblclick.stop="startEditRelationName(relation)">
              </polygon>
              
              <!-- 关系名称（在菱形内） -->
              <text
                v-if="editingRelation !== relation || editingRelationField !== 'name'"
                :x="relation.diamondX"
                :y="relation.diamondY"
                text-anchor="middle"
                dominant-baseline="middle"
                font-family="宋体"
                font-size="18"
                fill="#000000"
                @dblclick.stop="startEditRelationName(relation)"
                pointer-events="none">
                {{ relation.name }}
              </text>
              <foreignObject
                v-if="editingRelation === relation && editingRelationField === 'name'"
                :x="relation.diamondX - relation.diamondWidth * 0.8"
                :y="relation.diamondY - 15"
                :width="relation.diamondWidth * 1.6"
                height="30">
                <input
                  type="text"
                  v-model="editingRelationText"
                  @blur="finishEditRelation"
                  @keyup.enter="finishEditRelation"
                  @keyup.esc="cancelEditRelation"
                  ref="editRelationInput"
                  class="edit-input"
                  style="width: 100%; height: 100%; text-align: center; font-size: 18px; font-family: 宋体; border: 2px solid #409eff; outline: none;">
              </foreignObject>
              
              <!-- 源端基数（在线条中间） -->
              <text
                v-if="editingRelation !== relation || editingRelationField !== 'sourceCardinality'"
                :x="relation.sourceCardinalityX"
                :y="relation.sourceCardinalityY"
                text-anchor="middle"
                dominant-baseline="middle"
                font-family="Arial, sans-serif"
                font-size="18"
                font-weight="300"
                fill="#000000"
                @dblclick.stop="startEditSourceCardinality(relation)"
                class="editable-text"
                style="font-weight: 300;">
                {{ relation.sourceCardinality }}
              </text>
              <foreignObject
                v-if="editingRelation === relation && editingRelationField === 'sourceCardinality'"
                :x="relation.sourceCardinalityX - 20"
                :y="relation.sourceCardinalityY - 15"
                width="40"
                height="30">
                <input
                  type="text"
                  v-model="editingRelationText"
                  @blur="finishEditRelation"
                  @keyup.enter="finishEditRelation"
                  @keyup.esc="cancelEditRelation"
                  ref="editRelationInput"
                  class="edit-input"
                  style="width: 100%; height: 100%; text-align: center; font-size: 18px; font-family: Arial; font-weight: 300; border: 2px solid #409eff; outline: none;">
              </foreignObject>
              
              <!-- 目标端基数（在线条中间） -->
              <text
                v-if="editingRelation !== relation || editingRelationField !== 'targetCardinality'"
                :x="relation.targetCardinalityX"
                :y="relation.targetCardinalityY"
                text-anchor="middle"
                dominant-baseline="middle"
                font-family="Arial, sans-serif"
                font-size="18"
                font-weight="300"
                fill="#000000"
                @dblclick.stop="startEditTargetCardinality(relation)"
                class="editable-text"
                style="font-weight: 300;">
                {{ relation.targetCardinality }}
              </text>
              <foreignObject
                v-if="editingRelation === relation && editingRelationField === 'targetCardinality'"
                :x="relation.targetCardinalityX - 20"
                :y="relation.targetCardinalityY - 15"
                width="40"
                height="30">
                <input
                  type="text"
                  v-model="editingRelationText"
                  @blur="finishEditRelation"
                  @keyup.enter="finishEditRelation"
                  @keyup.esc="cancelEditRelation"
                  ref="editRelationInput"
                  class="edit-input"
                  style="width: 100%; height: 100%; text-align: center; font-size: 16px; font-family: Arial; border: 2px solid #409eff; outline: none;">
              </foreignObject>
            </g>
          </g>

          <!-- 属性节点（椭圆） -->
          <g class="attributes">
            <g v-for="attr in attributes" :key="attr.key" v-show="attr.visible">
              <ellipse
                :cx="attr.x"
                :cy="attr.y"
                :rx="attr.rx"
                :ry="attr.ry"
                stroke="#000000"
                stroke-width="1"
                fill="white"
                @mousedown.stop="startDragNode($event, attr)"
                @dblclick.stop="startEditText(attr)"
                class="draggable-node">
              </ellipse>
              <text
                v-if="editingNode !== attr"
                :x="attr.x"
                :y="attr.y"
                text-anchor="middle"
                dominant-baseline="middle"
                font-family="宋体"
                font-size="18"
                fill="#333"
                @mousedown.stop="startDragNode($event, attr)"
                @dblclick.stop="startEditText(attr)"
                pointer-events="none">
                {{ truncateText(attr.name, 6) }}
              </text>
              <foreignObject
                v-if="editingNode === attr"
                :x="attr.x - attr.rx"
                :y="attr.y - attr.ry"
                :width="attr.rx * 2"
                :height="attr.ry * 2">
                <input
                  type="text"
                  v-model="editingText"
                  @blur="finishEdit"
                  @keyup.enter="finishEdit"
                  @keyup.esc="cancelEdit"
                  ref="editInput"
                  class="edit-input"
                  style="width: 100%; height: 100%; text-align: center; font-size: 18px; font-family: 宋体; border: 2px solid #409eff; outline: none; border-radius: 50%;">
              </foreignObject>
              <!-- 调整大小手柄 -->
              <circle
                v-if="selectedNode === attr"
                :cx="attr.x + attr.rx"
                :cy="attr.y"
                r="6"
                fill="#409eff"
                stroke="white"
                stroke-width="2"
                @mousedown.stop="startResize($event, attr)"
                class="resize-handle">
              </circle>
              <!-- 主键下划线 -->
              <line
                v-if="attr.isPrimaryKey && editingNode !== attr"
                :x1="attr.x - truncateText(attr.name, 6).length * 8"
                :y1="attr.y + 8"
                :x2="attr.x + truncateText(attr.name, 6).length * 8"
                :y2="attr.y + 8"
                stroke="#333"
                stroke-width="1"
                pointer-events="none">
              </line>
            </g>
          </g>
        </svg>
      </div>
    </div>

    <ExportDialog
      :visible.sync="isExportDialogVisible"
      @confirm="handleExportConfirm"
    />

    <!-- 实体选择对话框 -->
    <el-dialog
      :visible.sync="isEntitySelectVisible"
      title="选择要显示的实体"
      width="400px">
      <div class="entity-select-header">
        <el-button type="text" @click="selectAllEntities">全选</el-button>
        <el-button type="text" @click="invertSelection">反选</el-button>
        <el-button type="text" @click="selectedEntities = []">清空</el-button>
      </div>
      <el-checkbox-group v-model="selectedEntities" class="entity-checkbox-group">
        <el-checkbox
          v-for="entity in availableEntities"
          :key="entity.key"
          :label="entity.key">
          {{ entity.name }}
        </el-checkbox>
      </el-checkbox-group>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="isEntitySelectVisible = false">取消</el-button>
          <el-button type="primary" @click="applyEntityFilter">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import ExportDialog from './ExportDialog.vue'

export default {
  name: 'ErAll',
  components: { ExportDialog },
  data() {
    return {
      svgWidth: 3000,
      svgHeight: 3000,
      entities: [],
      attributes: [],
      attributeInitialPositions: [], // 存储属性相对于实体的初始偏移
      links: [],
      foreignKeyRelations: [], // 存储外键关系
      isExportDialogVisible: false,
      isFullscreen: false,
      isEntitySelectVisible: false,
      availableEntities: [],
      selectedEntities: [],
      draggingNode: null,
      dragStartX: 0,
      dragStartY: 0,
      mouseStartX: 0,
      mouseStartY: 0,
      draggingRelation: null, // 当前拖动的关系菱形
      isPanning: false,
      panStartX: 0,
      panStartY: 0,
      // 缩放和平移
      zoomLevel: 1,
      panX: 0,
      panY: 0,
      minZoom: 0.1,
      maxZoom: 10,
      editingNode: null,
      editingText: '',
      resizingNode: null,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 0,
      resizeStartHeight: 0,
      resizeStartRx: 0,
      resizeStartRy: 0,
      selectedNode: null, // 当前选中的节点
      editingRelation: null, // 当前编辑的关系
      editingRelationField: null, // 当前编辑的字段：'name', 'sourceCardinality', 'targetCardinality'
      editingRelationText: '' // 编辑的文本
    }
  },
  computed: {
    zoomedViewBox() {
      const width = this.svgWidth / this.zoomLevel
      const height = this.svgHeight / this.zoomLevel
      const x = -this.panX / this.zoomLevel
      const y = -this.panY / this.zoomLevel
      return `${x} ${y} ${width} ${height}`
    }
  },
  mounted() {
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement
    })
  },
  methods: {
    updateDiagram(tables) {
      console.log('开始生成总体ER图，表数量:', tables.length)
      
      // 验证数据
      if (!tables || !Array.isArray(tables) || tables.length === 0) {
        this.$message.error('没有可用的表数据')
        return
      }
      
      try {
        this.entities = []
        this.attributes = []
        this.attributeInitialPositions = []
        this.links = []
        this.foreignKeyRelations = []

        const TOP_MARGIN = 300 // 顶部边距
        const centerX = 1500
        const centerY = 1500

        // 第一步：找出有最多外键关系的表作为中心表
        const tableFKCount = {}
        tables.forEach(table => {
          tableFKCount[table.tableName] = 0
        })
        
        tables.forEach(table => {
          if (table.foreignKeys && table.foreignKeys.length > 0) {
            table.foreignKeys.forEach(fk => {
              tableFKCount[table.tableName]++
              tableFKCount[fk.referenceTable]++
            })
          }
        })
        
        // 优先选择用户表作为中心表
        let centerTable = null
        
        // 尝试通过多种方式识别用户表
        const userTablePatterns = ['user', '用户', 'sys_user', 'member', '会员', 'account', '账号']
        
        for (const pattern of userTablePatterns) {
          centerTable = tables.find(t => 
            t.tableName.toLowerCase().includes(pattern.toLowerCase()) ||
            (t.comment && t.comment.includes(pattern))
          )
          if (centerTable) {
            console.log(`找到用户表作为中心: ${centerTable.tableName}`)
            break
          }
        }
        
        // 如果没找到用户表，则选择关系最多的表
        if (!centerTable) {
          centerTable = tables[0]
          let maxCount = 0
          for (const tableName in tableFKCount) {
            if (tableFKCount[tableName] > maxCount) {
              maxCount = tableFKCount[tableName]
              centerTable = tables.find(t => t.tableName === tableName)
            }
          }
          console.log(`使用关系最多的表作为中心: ${centerTable.tableName}`)
        }

        // 第二步：使用改进的力导向布局算法 - 让有关系的实体靠近，减少交叉
        const tablePositions = {}
        
        // 计算每个表与中心表的关系强度
        const relationStrength = {}
        tables.forEach(table => {
          relationStrength[table.tableName] = 0
        })
        
        // 统计与中心表的直接关系
        tables.forEach(table => {
          if (table.foreignKeys && table.foreignKeys.length > 0) {
            table.foreignKeys.forEach(fk => {
              if (fk.referenceTable === centerTable.tableName) {
                relationStrength[table.tableName]++
              }
              if (table.tableName === centerTable.tableName) {
                relationStrength[fk.referenceTable]++
              }
            })
          }
        })
        
        // 初始化位置 - 中心表在中间
        tablePositions[centerTable.tableName] = { x: centerX - 100, y: centerY - 25 }
        
        // 将其他表按关系强度分层
        const otherTables = tables.filter(t => t.tableName !== centerTable.tableName)
        
        // 按关系强度排序（关系强的放在内圈）
        otherTables.sort((a, b) => {
          const strengthA = relationStrength[a.tableName]
          const strengthB = relationStrength[b.tableName]
          if (strengthA !== strengthB) {
            return strengthB - strengthA // 关系强的排前面
          }
          // 关系强度相同，按外键数量排序
          const fkCountA = (a.foreignKeys || []).length
          const fkCountB = (b.foreignKeys || []).length
          return fkCountB - fkCountA
        })
        
        // 分层布局：关系强的在内圈，关系弱的在外圈
        const innerRadius = 800  // 增大内圈半径
        const outerRadius = 1400 // 增大外圈半径
        
        otherTables.forEach((table, idx) => {
          const strength = relationStrength[table.tableName]
          const r = strength > 0 ? innerRadius : outerRadius
          const angle = idx * (2 * Math.PI / otherTables.length) - Math.PI / 2
          const x = centerX + r * Math.cos(angle) - 100
          const y = centerY + r * Math.sin(angle) - 25
          tablePositions[table.tableName] = { x, y }
        })
        
        // 力导向算法优化位置
        const iterations = 200 // 增加迭代次数
        const repulsionForce = 150000 // 大幅增大排斥力
        const attractionForce = 0.008 // 减小吸引力
        const damping = 0.88 // 增大阻尼系数
        
        // 为每个表创建速度
        const velocities = {}
        tables.forEach(table => {
          velocities[table.tableName] = { vx: 0, vy: 0 }
        })
        
        for (let iter = 0; iter < iterations; iter++) {
          // 计算每个表受到的力
          const forces = {}
          tables.forEach(table => {
            forces[table.tableName] = { fx: 0, fy: 0 }
          })
        
        // 1. 排斥力 - 所有表之间互相排斥
        for (let i = 0; i < tables.length; i++) {
          for (let j = i + 1; j < tables.length; j++) {
            const table1 = tables[i]
            const table2 = tables[j]
            
            const pos1 = tablePositions[table1.tableName]
            const pos2 = tablePositions[table2.tableName]
            
            const dx = pos2.x - pos1.x
            const dy = pos2.y - pos1.y
            const distance = Math.max(Math.sqrt(dx * dx + dy * dy), 100) // 增大最小距离到100
            
            // 排斥力与距离平方成反比
            const force = repulsionForce / (distance * distance)
            const fx = (dx / distance) * force
            const fy = (dy / distance) * force
            
            forces[table1.tableName].fx -= fx
            forces[table1.tableName].fy -= fy
            forces[table2.tableName].fx += fx
            forces[table2.tableName].fy += fy
          }
        }
        
        // 2. 吸引力 - 有外键关系的表之间互相吸引
        tables.forEach(table => {
          if (table.foreignKeys && table.foreignKeys.length > 0) {
            table.foreignKeys.forEach(fk => {
              const sourceTable = table
              const targetTable = tables.find(t => t.tableName === fk.referenceTable)
              
              if (targetTable && tablePositions[targetTable.tableName]) {
                const pos1 = tablePositions[sourceTable.tableName]
                const pos2 = tablePositions[targetTable.tableName]
                
                const dx = pos2.x - pos1.x
                const dy = pos2.y - pos1.y
                const distance = Math.sqrt(dx * dx + dy * dy) || 1
                
                // 吸引力与距离成正比，但有上限
                const idealDistance = 600 // 增大理想距离到600
                const force = Math.min(distance - idealDistance, 800) * attractionForce
                const fx = (dx / distance) * force
                const fy = (dy / distance) * force
                
                forces[sourceTable.tableName].fx += fx
                forces[sourceTable.tableName].fy += fy
                forces[targetTable.tableName].fx -= fx
                forces[targetTable.tableName].fy -= fy
              }
            })
          }
        })
        
        // 3. 中心表保持在中心（强制固定）
        forces[centerTable.tableName].fx = 0
        forces[centerTable.tableName].fy = 0
        velocities[centerTable.tableName].vx = 0
        velocities[centerTable.tableName].vy = 0
        
        // 4. 向中心的轻微吸引力（防止表飞出去）
        tables.forEach(table => {
          if (table.tableName === centerTable.tableName) return
          
          const pos = tablePositions[table.tableName]
          const dx = centerX - pos.x
          const dy = centerY - pos.y
          const distance = Math.sqrt(dx * dx + dy * dy) || 1
          
          // 如果距离中心太远，施加向心力
          if (distance > 1000) {
            const force = (distance - 1000) * 0.005
            forces[table.tableName].fx += (dx / distance) * force
            forces[table.tableName].fy += (dy / distance) * force
          }
        })
        
        // 5. 更新速度和位置
        tables.forEach(table => {
          if (table.tableName === centerTable.tableName) return // 中心表不移动
          
          const force = forces[table.tableName]
          const velocity = velocities[table.tableName]
          
          // 更新速度（加上力，乘以阻尼）
          velocity.vx = (velocity.vx + force.fx) * damping
          velocity.vy = (velocity.vy + force.fy) * damping
          
          // 检查并修复 NaN 值
          if (isNaN(velocity.vx) || !isFinite(velocity.vx)) velocity.vx = 0
          if (isNaN(velocity.vy) || !isFinite(velocity.vy)) velocity.vy = 0
          
          // 限制最大速度
          const maxSpeed = 30
          const speed = Math.sqrt(velocity.vx * velocity.vx + velocity.vy * velocity.vy)
          if (speed > maxSpeed) {
            velocity.vx = (velocity.vx / speed) * maxSpeed
            velocity.vy = (velocity.vy / speed) * maxSpeed
          }
          
          // 更新位置
          const pos = tablePositions[table.tableName]
          if (pos) {
            pos.x += velocity.vx
            pos.y += velocity.vy
            
            // 检查并修复 NaN 值
            if (isNaN(pos.x) || !isFinite(pos.x)) pos.x = centerX
            if (isNaN(pos.y) || !isFinite(pos.y)) pos.y = centerY
            
            // 限制在画布范围内
            pos.x = Math.max(200, Math.min(2700, pos.x))
            pos.y = Math.max(200, Math.min(2700, pos.y))
          }
        })
        }
        
        console.log('力导向布局完成，优化了表的位置以减少交叉')

        // 第三步：创建实体节点
        tables.forEach(table => {
          const pos = tablePositions[table.tableName]
          
          this.entities.push({
            key: table.tableName,
            name: this.removeQuotes(table.comment) || table.tableName,
            x: pos.x,
            y: pos.y,
            width: 200,
            height: 50,
            visible: true
          })
        })

        // 第四步：智能布局属性 - 避免遮挡其他元素
        tables.forEach(table => {
          const pos = tablePositions[table.tableName]
          const entityCenterX = pos.x + 100
        const entityCenterY = pos.y + 25
        
        // 添加属性节点
        const cols = table.columns || []
        const n = cols.length
        if (n > 0) {
          const attrRadius = 280 // 增大属性距离实体的半径
          
          // 尝试八个方向：上、右上、右、右下、下、左下、左、左上
          const directions = [
            { name: 'top', startAngle: -120 * Math.PI / 180, endAngle: -60 * Math.PI / 180 },
            { name: 'top-right', startAngle: -60 * Math.PI / 180, endAngle: 0 },
            { name: 'right', startAngle: -30 * Math.PI / 180, endAngle: 30 * Math.PI / 180 },
            { name: 'bottom-right', startAngle: 0, endAngle: 60 * Math.PI / 180 },
            { name: 'bottom', startAngle: 60 * Math.PI / 180, endAngle: 120 * Math.PI / 180 },
            { name: 'bottom-left', startAngle: 120 * Math.PI / 180, endAngle: 180 * Math.PI / 180 },
            { name: 'left', startAngle: 150 * Math.PI / 180, endAngle: 210 * Math.PI / 180 },
            { name: 'top-left', startAngle: -180 * Math.PI / 180, endAngle: -120 * Math.PI / 180 }
          ]
          
          // 为每个方向计算碰撞分数（分数越低越好）
          let bestDirection = directions[0]
          let minCollisionScore = Infinity
          
          directions.forEach(direction => {
            let collisionScore = 0
            const totalAngle = direction.endAngle - direction.startAngle
            const attrAngleStep = n > 1 ? totalAngle / (n - 1) : 0
            
            // 计算该方向下所有属性的位置
            const testPositions = []
            cols.forEach((col, idx) => {
              const angle = n > 1 ? direction.startAngle + idx * attrAngleStep : (direction.startAngle + direction.endAngle) / 2
              const ax = entityCenterX + attrRadius * Math.cos(angle)
              const ay = entityCenterY + attrRadius * Math.sin(angle)
              const attrName = this.removeQuotes(col.comment) || col.name

              // 根据截断后的文字长度动态计算椭圆大小
              const displayName = this.truncateText(attrName, 6)
              const textLength = displayName.length
              const rx = Math.max(60, textLength * 12 + 20) // 横向半径根据显示文字长度
              const ry = 30
              testPositions.push({ x: ax, y: ay, rx, ry })
            })
            
            // 检测与其他实体的碰撞
            this.entities.forEach(otherEntity => {
              if (otherEntity.key === table.tableName) return // 跳过自己
              
              testPositions.forEach(testPos => {
                // 检测椭圆与矩形的碰撞
                const collision = this.checkEllipseRectCollision(
                  testPos.x, testPos.y, testPos.rx, testPos.ry,
                  otherEntity.x, otherEntity.y, otherEntity.width, otherEntity.height
                )
                if (collision) {
                  collisionScore += 100 // 严重碰撞
                }
                
                // 计算距离，距离越近分数越高
                const distance = this.getDistanceEllipseToRect(
                  testPos.x, testPos.y, testPos.rx, testPos.ry,
                  otherEntity.x, otherEntity.y, otherEntity.width, otherEntity.height
                )
                if (distance < 150) {
                  collisionScore += (150 - distance) / 5 // 增加距离惩罚权重
                }
              })
            })
            
            // 检测与外键关系菱形的碰撞（如果已经创建）
            if (this.foreignKeyRelations && this.foreignKeyRelations.length > 0) {
              this.foreignKeyRelations.forEach(relation => {
                testPositions.forEach(testPos => {
                  const dx = testPos.x - relation.diamondX
                  const dy = testPos.y - relation.diamondY
                  const distance = Math.sqrt(dx * dx + dy * dy)
                  if (distance < testPos.rx + relation.diamondWidth + 50) {
                    collisionScore += 80 // 增加与菱形碰撞的惩罚
                  }
                })
              })
            }
            
            // 检测属性之间的碰撞
            this.attributes.forEach(existingAttr => {
              testPositions.forEach(testPos => {
                const dx = testPos.x - existingAttr.x
                const dy = testPos.y - existingAttr.y
                const minDistance = testPos.rx + existingAttr.rx + 20
                const distance = Math.sqrt(dx * dx + dy * dy)
                if (distance < minDistance) {
                  collisionScore += 60 // 属性之间碰撞惩罚
                }
              })
            })
            
            // 如果是中心表，优先选择下方
            if (table.tableName === centerTable.tableName && direction.name === 'bottom') {
              collisionScore -= 30 // 给下方加分
            }
            
            // 根据实体相对于中心的位置给方向加分 - 属性朝外分布
            const dx = entityCenterX - centerX
            const dy = entityCenterY - centerY
            const entityAngle = Math.atan2(dy, dx)
            
            // 计算方向的中心角度
            const directionCenterAngle = (direction.startAngle + direction.endAngle) / 2
            
            // 计算方向角度与实体角度的差异
            let angleDiff = Math.abs(directionCenterAngle - entityAngle)
            if (angleDiff > Math.PI) {
              angleDiff = 2 * Math.PI - angleDiff
            }
            
            // 角度差异越小，说明方向越朝外，给予奖励
            if (angleDiff < Math.PI / 3) {
              collisionScore -= 25 // 朝外方向奖励
            }
            
            if (collisionScore < minCollisionScore) {
              minCollisionScore = collisionScore
              bestDirection = direction
            }
          })
          
          // 使用最佳方向布局属性
          const totalAngle = bestDirection.endAngle - bestDirection.startAngle
          const attrAngleStep = n > 1 ? totalAngle / (n - 1) : 0

          cols.forEach((col, idx) => {
            const angle = n > 1 ? bestDirection.startAngle + idx * attrAngleStep : (bestDirection.startAngle + bestDirection.endAngle) / 2
            const ax = entityCenterX + attrRadius * Math.cos(angle)
            const ay = entityCenterY + attrRadius * Math.sin(angle)
            const attrKey = `${table.tableName}-${col.name}`
            const attrName = this.removeQuotes(col.comment) || col.name

            // 根据截断后的文字长度动态计算椭圆大小
            const displayName = this.truncateText(attrName, 6)
            const textLength = displayName.length
            const rx = Math.max(60, textLength * 12 + 20) // 横向半径根据显示文字长度
            const ry = 30

            this.attributes.push({
              key: attrKey,
              name: attrName,
              x: ax,
              y: ay,
              rx: rx,
              ry: ry,
              isPrimaryKey: table.primaryKeys.includes(col.name),
              entityKey: table.tableName,
              visible: true
            })

            this.attributeInitialPositions.push({
              key: attrKey,
              offsetX: attrRadius * Math.cos(angle),
              offsetY: attrRadius * Math.sin(angle)
            })

            this.links.push({
              id: `link-${attrKey}`,
              x1: entityCenterX,
              y1: entityCenterY,
              x2: ax,
              y2: ay
            })
          })
        }
      })

      // 添加外键关系
      // 添加外键关系
      tables.forEach(table => {
        if (table.foreignKeys && table.foreignKeys.length > 0) {
          table.foreignKeys.forEach((fk, idx) => {
            const sourceEntity = this.entities.find(e => e.key === table.tableName)
            const targetEntity = this.entities.find(e => e.key === fk.referenceTable)
            
            if (sourceEntity && targetEntity) {
              const relation = this.createForeignKeyRelation(
                sourceEntity,
                targetEntity,
                fk,
                `fk-${table.tableName}-${fk.columnName}-${idx}`
              )
              this.foreignKeyRelations.push(relation)
            }
          })
        }
      })
      
      console.log('总体ER图生成完成')
      console.log('实体数量:', this.entities.length)
      console.log('属性数量:', this.attributes.length)
      console.log('关系数量:', this.foreignKeyRelations.length)
      
    } catch (error) {
      console.error('生成ER图时出错:', error)
      this.$message.error('生成ER图失败: ' + error.message)
    }
  },

    createForeignKeyRelation(sourceEntity, targetEntity, foreignKey, id) {
      // 计算源实体和目标实体的中心点
      const sourceCenterX = sourceEntity.x + sourceEntity.width / 2
      const sourceCenterY = sourceEntity.y + sourceEntity.height / 2
      const targetCenterX = targetEntity.x + targetEntity.width / 2
      const targetCenterY = targetEntity.y + targetEntity.height / 2

      // 计算菱形位置（在两个实体中间，但稍微偏移避免与连线重叠）
      let diamondX = (sourceCenterX + targetCenterX) / 2
      let diamondY = (sourceCenterY + targetCenterY) / 2
      
      // 计算连线的垂直方向，将菱形稍微偏移
      const dx = targetCenterX - sourceCenterX
      const dy = targetCenterY - sourceCenterY
      const lineLength = Math.sqrt(dx * dx + dy * dy)
      
      if (lineLength > 0) {
        // 垂直于连线的方向
        const perpX = -dy / lineLength
        const perpY = dx / lineLength
        
        // 将菱形向垂直方向偏移30像素，避免与连线重叠
        const offset = 30
        diamondX += perpX * offset
        diamondY += perpY * offset
      }

      // 默认关系名称
      const relationName = '关联'
      
      // 根据文字长度计算菱形大小 - 扁平化（宽度大于高度）
      const textLength = relationName.length
      const diamondWidth = Math.max(50, textLength * 10 + 20) // 根据文字长度调整宽度
      const diamondHeight = 30 // 固定高度

      // 计算菱形的四个顶点（扁平菱形）
      const diamondPoints = `
        ${diamondX},${diamondY - diamondHeight}
        ${diamondX + diamondWidth},${diamondY}
        ${diamondX},${diamondY + diamondHeight}
        ${diamondX - diamondWidth},${diamondY}
      `.trim()

      // 计算菱形到源实体和目标实体的连接点（菱形的顶点）
      const diamondSourcePoint = this.getDiamondConnectionPoint(
        diamondX, diamondY, diamondWidth, diamondHeight,
        sourceCenterX, sourceCenterY
      )
      const diamondTargetPoint = this.getDiamondConnectionPoint(
        diamondX, diamondY, diamondWidth, diamondHeight,
        targetCenterX, targetCenterY
      )

      // 计算线条中点位置
      const sourceMidX = sourceCenterX + (diamondSourcePoint.x - sourceCenterX) * 0.5
      const sourceMidY = sourceCenterY + (diamondSourcePoint.y - sourceCenterY) * 0.5
      const targetMidX = targetCenterX + (diamondTargetPoint.x - targetCenterX) * 0.5
      const targetMidY = targetCenterY + (diamondTargetPoint.y - targetCenterY) * 0.5

      // 计算线条的垂直方向（用于将文字放在线条上方）
      const textOffset = 20 // 增加文字距离线条的距离

      // 源端：计算垂直于线条的偏移
      const sourceDx = diamondSourcePoint.x - sourceCenterX
      const sourceDy = diamondSourcePoint.y - sourceCenterY
      const sourceLen = Math.sqrt(sourceDx * sourceDx + sourceDy * sourceDy)
      const sourceNormalX = sourceLen > 0 ? -sourceDy / sourceLen : 0
      const sourceNormalY = sourceLen > 0 ? sourceDx / sourceLen : 0

      // 目标端：计算垂直于线条的偏移
      const targetDx = diamondTargetPoint.x - targetCenterX
      const targetDy = diamondTargetPoint.y - targetCenterY
      const targetLen = Math.sqrt(targetDx * targetDx + targetDy * targetDy)
      const targetNormalX = targetLen > 0 ? -targetDy / targetLen : 0
      const targetNormalY = targetLen > 0 ? targetDx / targetLen : 0

      // 基数标注位置（在线条上方）
      const sourceCardinalityX = sourceMidX + sourceNormalX * textOffset
      const sourceCardinalityY = sourceMidY + sourceNormalY * textOffset
      const targetCardinalityX = targetMidX + targetNormalX * textOffset
      const targetCardinalityY = targetMidY + targetNormalY * textOffset

      return {
        id: id,
        name: relationName,
        sourceX: sourceCenterX,
        sourceY: sourceCenterY,
        targetX: targetCenterX,
        targetY: targetCenterY,
        diamondX: diamondX,
        diamondY: diamondY,
        diamondWidth: diamondWidth,
        diamondHeight: diamondHeight,
        diamondPoints: diamondPoints,
        diamondSourceX: diamondSourcePoint.x,
        diamondSourceY: diamondSourcePoint.y,
        diamondTargetX: diamondTargetPoint.x,
        diamondTargetY: diamondTargetPoint.y,
        sourceCardinality: 'N',
        targetCardinality: '1',
        sourceCardinalityX: sourceCardinalityX,
        sourceCardinalityY: sourceCardinalityY,
        targetCardinalityX: targetCardinalityX,
        targetCardinalityY: targetCardinalityY,
        visible: true,
        sourceEntityKey: sourceEntity.key,
        targetEntityKey: targetEntity.key,
        isManuallyPositioned: false // 标记菱形是否被手动移动过
      }
    },

    removeQuotes(str) {
      if (!str) return str
      return str.replace(/^'|'$/g, '')
    },

    truncateText(text, maxLength = 6) {
      if (!text) return text
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    // 计算菱形上距离目标点最近的顶点
    getDiamondConnectionPoint(diamondX, diamondY, diamondWidth, diamondHeight, targetX, targetY) {
      // 菱形的四个顶点
      const points = [
        { x: diamondX, y: diamondY - diamondHeight, name: 'top' },           // 上
        { x: diamondX + diamondWidth, y: diamondY, name: 'right' },          // 右
        { x: diamondX, y: diamondY + diamondHeight, name: 'bottom' },        // 下
        { x: diamondX - diamondWidth, y: diamondY, name: 'left' }            // 左
      ]
      
      // 计算每个顶点到目标点的距离，选择最近的
      let minDistance = Infinity
      let bestPoint = points[0]
      
      points.forEach(point => {
        const dx = point.x - targetX
        const dy = point.y - targetY
        const distance = Math.sqrt(dx * dx + dy * dy)
        
        if (distance < minDistance) {
          minDistance = distance
          bestPoint = point
        }
      })
      
      return bestPoint
    },

    isLinkVisible(link) {
      // 检查连接线对应的属性和实体是否都可见
      const attrKey = link.id.replace('link-', '')
      const attr = this.attributes.find(a => a.key === attrKey)
      if (!attr || !attr.visible) return false
      
      const entity = this.entities.find(e => e.key === attr.entityKey)
      return entity && entity.visible
    },

    startDragNode(event, node) {
      event.stopPropagation()
      this.selectedNode = node // 选中节点
      this.draggingNode = node
      
      // 记录节点当前位置和鼠标位置的偏移
      if (node.width) {
        // 实体节点
        this.dragStartX = node.x
        this.dragStartY = node.y
      } else {
        // 属性节点
        this.dragStartX = node.x
        this.dragStartY = node.y
      }
      
      // 记录鼠标起始位置（SVG坐标系）
      const svg = this.$el.querySelector('.diagram-svg')
      const pt = svg.createSVGPoint()
      pt.x = event.clientX
      pt.y = event.clientY
      const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
      this.mouseStartX = svgP.x
      this.mouseStartY = svgP.y
    },

    startDragRelation(event, relation) {
      event.stopPropagation()
      this.draggingRelation = relation
      
      // 记录菱形当前位置
      this.dragStartX = relation.diamondX
      this.dragStartY = relation.diamondY
      
      // 记录鼠标起始位置（SVG坐标系）
      const svg = this.$el.querySelector('.diagram-svg')
      const pt = svg.createSVGPoint()
      pt.x = event.clientX
      pt.y = event.clientY
      const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
      this.mouseStartX = svgP.x
      this.mouseStartY = svgP.y
    },

    startPan(event) {
      // 只有在没有拖拽节点时才允许拖拽画布
      if (!this.draggingNode && event.target.classList.contains('diagram-svg')) {
        this.selectedNode = null // 取消选中
        this.isPanning = true
        this.panStartX = event.clientX
        this.panStartY = event.clientY
      }
    },

    onDrag(event) {
      if (this.resizingNode) {
        // 调整节点大小
        const svg = this.$el.querySelector('.diagram-svg')
        const pt = svg.createSVGPoint()
        pt.x = event.clientX
        pt.y = event.clientY
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
        
        if (this.resizingNode.width) {
          // 实体节点 - 调整宽度和高度
          const newWidth = Math.max(100, svgP.x - this.resizingNode.x)
          const newHeight = Math.max(30, svgP.y - this.resizingNode.y)
          this.resizingNode.width = newWidth
          this.resizingNode.height = newHeight
        } else {
          // 属性节点 - 调整椭圆半径
          const dx = svgP.x - this.resizingNode.x
          const dy = svgP.y - this.resizingNode.y
          this.resizingNode.rx = Math.max(30, Math.abs(dx))
          this.resizingNode.ry = Math.max(20, Math.abs(dy))
        }
        
        this.updateLinks()
      } else if (this.draggingRelation) {
        // 拖动关系菱形
        const svg = this.$el.querySelector('.diagram-svg')
        const pt = svg.createSVGPoint()
        pt.x = event.clientX
        pt.y = event.clientY
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
        
        // 计算偏移量
        const dx = svgP.x - this.mouseStartX
        const dy = svgP.y - this.mouseStartY
        
        // 更新菱形位置
        const newDiamondX = this.dragStartX + dx
        const newDiamondY = this.dragStartY + dy
        this.draggingRelation.diamondX = newDiamondX
        this.draggingRelation.diamondY = newDiamondY
        
        // 更新菱形顶点
        const diamondWidth = this.draggingRelation.diamondWidth || 50
        const diamondHeight = this.draggingRelation.diamondHeight || 30
        this.draggingRelation.diamondPoints = `
          ${newDiamondX},${newDiamondY - diamondHeight}
          ${newDiamondX + diamondWidth},${newDiamondY}
          ${newDiamondX},${newDiamondY + diamondHeight}
          ${newDiamondX - diamondWidth},${newDiamondY}
        `.trim()
        
        // 获取源实体和目标实体
        const sourceEntity = this.entities.find(e => e.key === this.draggingRelation.sourceEntityKey)
        const targetEntity = this.entities.find(e => e.key === this.draggingRelation.targetEntityKey)
        
        if (sourceEntity && targetEntity) {
          const sourceCenterX = sourceEntity.x + sourceEntity.width / 2
          const sourceCenterY = sourceEntity.y + sourceEntity.height / 2
          const targetCenterX = targetEntity.x + targetEntity.width / 2
          const targetCenterY = targetEntity.y + targetEntity.height / 2
          
          // 重新计算菱形到源实体和目标实体的连接点
          const diamondSourcePoint = this.getDiamondConnectionPoint(
            newDiamondX, newDiamondY, diamondWidth, diamondHeight,
            sourceCenterX, sourceCenterY
          )
          const diamondTargetPoint = this.getDiamondConnectionPoint(
            newDiamondX, newDiamondY, diamondWidth, diamondHeight,
            targetCenterX, targetCenterY
          )
          
          this.draggingRelation.diamondSourceX = diamondSourcePoint.x
          this.draggingRelation.diamondSourceY = diamondSourcePoint.y
          this.draggingRelation.diamondTargetX = diamondTargetPoint.x
          this.draggingRelation.diamondTargetY = diamondTargetPoint.y
        }
        
        // 更新基数标注位置
        this.updateRelationCardinality(this.draggingRelation)
      } else if (this.draggingNode) {
        // 获取当前鼠标在SVG坐标系中的位置
        const svg = this.$el.querySelector('.diagram-svg')
        const pt = svg.createSVGPoint()
        pt.x = event.clientX
        pt.y = event.clientY
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
        
        // 计算偏移量
        const dx = svgP.x - this.mouseStartX
        const dy = svgP.y - this.mouseStartY
        
        // 更新节点位置
        this.draggingNode.x = this.dragStartX + dx
        this.draggingNode.y = this.dragStartY + dy

        // 如果拖拽的是实体节点，同时移动其关联的属性节点
        if (this.draggingNode.width) {
          // 这是实体节点
          const entityKey = this.draggingNode.key
          this.attributes.forEach(attr => {
            if (attr.entityKey === entityKey) {
              // 找到该属性的初始位置（相对于实体的偏移）
              const attrInitialData = this.attributeInitialPositions.find(a => a.key === attr.key)
              if (attrInitialData) {
                attr.x = this.draggingNode.x + this.draggingNode.width / 2 + attrInitialData.offsetX
                attr.y = this.draggingNode.y + this.draggingNode.height / 2 + attrInitialData.offsetY
              }
            }
          })
        }

        // 更新连接线
        this.updateLinks()
      } else if (this.isPanning) {
        // 拖拽画布 - 大幅提升速度
        const dx = event.clientX - this.panStartX
        const dy = event.clientY - this.panStartY
        this.panX += dx * 5
        this.panY += dy * 5
        this.panStartX = event.clientX
        this.panStartY = event.clientY
      }
    },

    endDrag() {
      // 如果拖拽的是属性节点，更新其相对于实体的偏移量
      if (this.draggingNode && !this.draggingNode.width) {
        // 这是属性节点
        const attr = this.draggingNode
        const entity = this.entities.find(e => e.key === attr.entityKey)
        if (entity) {
          const entityCenterX = entity.x + entity.width / 2
          const entityCenterY = entity.y + entity.height / 2
          
          // 更新该属性的偏移量
          const attrInitialData = this.attributeInitialPositions.find(a => a.key === attr.key)
          if (attrInitialData) {
            attrInitialData.offsetX = attr.x - entityCenterX
            attrInitialData.offsetY = attr.y - entityCenterY
          }
        }
      }
      
      // 如果拖拽的是关系菱形，标记为手动定位
      if (this.draggingRelation) {
        this.draggingRelation.isManuallyPositioned = true
      }
      
      this.draggingNode = null
      this.draggingRelation = null
      this.resizingNode = null
      this.isPanning = false
    },

    handleWheel(event) {
      const delta = event.deltaY > 0 ? 0.9 : 1.1
      const newZoom = this.zoomLevel * delta
      
      if (newZoom >= this.minZoom && newZoom <= this.maxZoom) {
        const oldZoom = this.zoomLevel
        this.zoomLevel = newZoom
        
        // 以画布中心为缩放中心
        const zoomRatio = newZoom / oldZoom
        this.panX = this.panX * zoomRatio
        this.panY = this.panY * zoomRatio
      }
    },

    updateLinks() {
      // 更新属性连接线
      this.links.forEach(link => {
        const attrKey = link.id.replace('link-', '')
        const attr = this.attributes.find(a => a.key === attrKey)
        if (attr) {
          const entityKey = attr.entityKey
          const entity = this.entities.find(e => e.key === entityKey)
          if (entity) {
            link.x1 = entity.x + entity.width / 2
            link.y1 = entity.y + entity.height / 2
            link.x2 = attr.x
            link.y2 = attr.y
          }
        }
      })

      // 更新外键关系线条和标注
      this.foreignKeyRelations.forEach(relation => {
        const sourceEntity = this.entities.find(e => e.key === relation.sourceEntityKey)
        const targetEntity = this.entities.find(e => e.key === relation.targetEntityKey)
        
        if (sourceEntity && targetEntity) {
          // 重新计算源实体和目标实体的中心点
          const sourceCenterX = sourceEntity.x + sourceEntity.width / 2
          const sourceCenterY = sourceEntity.y + sourceEntity.height / 2
          const targetCenterX = targetEntity.x + targetEntity.width / 2
          const targetCenterY = targetEntity.y + targetEntity.height / 2

          // 更新连接点
          relation.sourceX = sourceCenterX
          relation.sourceY = sourceCenterY
          relation.targetX = targetCenterX
          relation.targetY = targetCenterY

          // 只有当菱形没有被手动移动过时，才重新计算菱形位置
          if (!relation.isManuallyPositioned) {
            // 重新计算菱形位置（在两个实体中间）
            const diamondX = (sourceCenterX + targetCenterX) / 2
            const diamondY = (sourceCenterY + targetCenterY) / 2
            relation.diamondX = diamondX
            relation.diamondY = diamondY

            // 菱形大小 - 扁平化（宽度大于高度）
            const diamondWidth = relation.diamondWidth || 50
            const diamondHeight = relation.diamondHeight || 30

            // 重新计算菱形的四个顶点（扁平菱形）
            relation.diamondPoints = `
              ${diamondX},${diamondY - diamondHeight}
              ${diamondX + diamondWidth},${diamondY}
              ${diamondX},${diamondY + diamondHeight}
              ${diamondX - diamondWidth},${diamondY}
            `.trim()
          } else {
            // 菱形被手动移动过，只更新菱形顶点（保持当前位置）
            const diamondX = relation.diamondX
            const diamondY = relation.diamondY
            const diamondWidth = relation.diamondWidth || 50
            const diamondHeight = relation.diamondHeight || 30

            relation.diamondPoints = `
              ${diamondX},${diamondY - diamondHeight}
              ${diamondX + diamondWidth},${diamondY}
              ${diamondX},${diamondY + diamondHeight}
              ${diamondX - diamondWidth},${diamondY}
            `.trim()
          }

          // 重新计算菱形到源实体和目标实体的连接点（菱形的顶点）
          const diamondX = relation.diamondX
          const diamondY = relation.diamondY
          const diamondWidth = relation.diamondWidth || 50
          const diamondHeight = relation.diamondHeight || 30
          
          const diamondSourcePoint = this.getDiamondConnectionPoint(
            diamondX, diamondY, diamondWidth, diamondHeight,
            sourceCenterX, sourceCenterY
          )
          const diamondTargetPoint = this.getDiamondConnectionPoint(
            diamondX, diamondY, diamondWidth, diamondHeight,
            targetCenterX, targetCenterY
          )
          
          relation.diamondSourceX = diamondSourcePoint.x
          relation.diamondSourceY = diamondSourcePoint.y
          relation.diamondTargetX = diamondTargetPoint.x
          relation.diamondTargetY = diamondTargetPoint.y

          // 计算线条中点位置
          const sourceMidX = sourceCenterX + (diamondSourcePoint.x - sourceCenterX) * 0.5
          const sourceMidY = sourceCenterY + (diamondSourcePoint.y - sourceCenterY) * 0.5
          const targetMidX = targetCenterX + (diamondTargetPoint.x - targetCenterX) * 0.5
          const targetMidY = targetCenterY + (diamondTargetPoint.y - targetCenterY) * 0.5

          // 计算线条的垂直方向（用于将文字放在线条上方）
          const textOffset = 15 // 文字距离线条的距离

          // 源端：计算垂直于线条的偏移
          const sourceDx = diamondSourcePoint.x - sourceCenterX
          const sourceDy = diamondSourcePoint.y - sourceCenterY
          const sourceLen = Math.sqrt(sourceDx * sourceDx + sourceDy * sourceDy)
          const sourceNormalX = sourceLen > 0 ? -sourceDy / sourceLen : 0
          const sourceNormalY = sourceLen > 0 ? sourceDx / sourceLen : 0

          // 目标端：计算垂直于线条的偏移
          const targetDx = diamondTargetPoint.x - targetCenterX
          const targetDy = diamondTargetPoint.y - targetCenterY
          const targetLen = Math.sqrt(targetDx * targetDx + targetDy * targetDy)
          const targetNormalX = targetLen > 0 ? -targetDy / targetLen : 0
          const targetNormalY = targetLen > 0 ? targetDx / targetLen : 0

          // 重新计算基数标注位置（在线条上方）
          relation.sourceCardinalityX = sourceMidX + sourceNormalX * textOffset
          relation.sourceCardinalityY = sourceMidY + sourceNormalY * textOffset
          relation.targetCardinalityX = targetMidX + targetNormalX * textOffset
          relation.targetCardinalityY = targetMidY + targetNormalY * textOffset
        }
      })
    },

    startEditText(node) {
      this.editingNode = node
      this.editingText = node.name
      this.$nextTick(() => {
        const input = this.$refs.editInput
        if (input) {
          if (Array.isArray(input)) {
            input[0].focus()
            input[0].select()
          } else {
            input.focus()
            input.select()
          }
        }
      })
    },

    finishEdit() {
      if (this.editingNode && this.editingText.trim()) {
        this.editingNode.name = this.editingText.trim()
        
        // 根据新文字长度调整节点大小
        const textLength = this.editingNode.name.length
        
        if (this.editingNode.width) {
          // 实体节点 - 调整矩形宽度
          const newWidth = Math.max(200, textLength * 18 + 40)
          const oldCenterX = this.editingNode.x + this.editingNode.width / 2
          this.editingNode.width = newWidth
          this.editingNode.x = oldCenterX - newWidth / 2 // 保持中心点不变
        } else {
          // 属性节点 - 调整椭圆横向半径
          this.editingNode.rx = Math.max(60, textLength * 11 + 20)
        }
        
        // 更新连接线
        this.updateLinks()
      }
      this.editingNode = null
      this.editingText = ''
    },

    cancelEdit() {
      this.editingNode = null
      this.editingText = ''
    },

    startEditRelationName(relation) {
      this.editingRelation = relation
      this.editingRelationField = 'name'
      this.editingRelationText = relation.name
      this.$nextTick(() => {
        const input = this.$refs.editRelationInput
        if (input) {
          if (Array.isArray(input)) {
            input[0].focus()
            input[0].select()
          } else {
            input.focus()
            input.select()
          }
        }
      })
    },

    startEditSourceCardinality(relation) {
      this.editingRelation = relation
      this.editingRelationField = 'sourceCardinality'
      this.editingRelationText = relation.sourceCardinality
      this.$nextTick(() => {
        const input = this.$refs.editRelationInput
        if (input) {
          if (Array.isArray(input)) {
            input[0].focus()
            input[0].select()
          } else {
            input.focus()
            input.select()
          }
        }
      })
    },

    startEditTargetCardinality(relation) {
      this.editingRelation = relation
      this.editingRelationField = 'targetCardinality'
      this.editingRelationText = relation.targetCardinality
      this.$nextTick(() => {
        const input = this.$refs.editRelationInput
        if (input) {
          if (Array.isArray(input)) {
            input[0].focus()
            input[0].select()
          } else {
            input.focus()
            input.select()
          }
        }
      })
    },

    finishEditRelation() {
      if (this.editingRelation && this.editingRelationText.trim()) {
        if (this.editingRelationField === 'name') {
          this.editingRelation.name = this.editingRelationText.trim()
          
          // 根据新文字长度重新计算菱形大小
          const textLength = this.editingRelation.name.length
          const newDiamondWidth = Math.max(50, textLength * 10 + 20)
          this.editingRelation.diamondWidth = newDiamondWidth
          
          // 重新计算菱形顶点
          const diamondX = this.editingRelation.diamondX
          const diamondY = this.editingRelation.diamondY
          const diamondHeight = this.editingRelation.diamondHeight
          
          this.editingRelation.diamondPoints = `
            ${diamondX},${diamondY - diamondHeight}
            ${diamondX + newDiamondWidth},${diamondY}
            ${diamondX},${diamondY + diamondHeight}
            ${diamondX - newDiamondWidth},${diamondY}
          `.trim()
          
        } else if (this.editingRelationField === 'sourceCardinality') {
          this.editingRelation.sourceCardinality = this.editingRelationText.trim()
        } else if (this.editingRelationField === 'targetCardinality') {
          this.editingRelation.targetCardinality = this.editingRelationText.trim()
        }
      }
      this.editingRelation = null
      this.editingRelationField = null
      this.editingRelationText = ''
    },

    cancelEditRelation() {
      this.editingRelation = null
      this.editingRelationField = null
      this.editingRelationText = ''
    },

    updateRelationCardinality(relation) {
      // 更新基数标注位置（当菱形移动时）
      const sourceEntity = this.entities.find(e => e.key === relation.sourceEntityKey)
      const targetEntity = this.entities.find(e => e.key === relation.targetEntityKey)
      
      if (sourceEntity && targetEntity) {
        const sourceCenterX = sourceEntity.x + sourceEntity.width / 2
        const sourceCenterY = sourceEntity.y + sourceEntity.height / 2
        const targetCenterX = targetEntity.x + targetEntity.width / 2
        const targetCenterY = targetEntity.y + targetEntity.height / 2
        const diamondX = relation.diamondX
        const diamondY = relation.diamondY
        
        // 使用菱形的连接点而不是中心点
        const diamondSourceX = relation.diamondSourceX
        const diamondSourceY = relation.diamondSourceY
        const diamondTargetX = relation.diamondTargetX
        const diamondTargetY = relation.diamondTargetY
        
        // 计算线条中点位置
        const sourceMidX = sourceCenterX + (diamondSourceX - sourceCenterX) * 0.5
        const sourceMidY = sourceCenterY + (diamondSourceY - sourceCenterY) * 0.5
        const targetMidX = targetCenterX + (diamondTargetX - targetCenterX) * 0.5
        const targetMidY = targetCenterY + (diamondTargetY - targetCenterY) * 0.5

        // 计算线条的垂直方向（用于将文字放在线条上方）
        const textOffset = 15

        // 源端：计算垂直于线条的偏移
        const sourceDx = diamondSourceX - sourceCenterX
        const sourceDy = diamondSourceY - sourceCenterY
        const sourceLen = Math.sqrt(sourceDx * sourceDx + sourceDy * sourceDy)
        const sourceNormalX = sourceLen > 0 ? -sourceDy / sourceLen : 0
        const sourceNormalY = sourceLen > 0 ? sourceDx / sourceLen : 0

        // 目标端：计算垂直于线条的偏移
        const targetDx = diamondTargetX - targetCenterX
        const targetDy = diamondTargetY - targetCenterY
        const targetLen = Math.sqrt(targetDx * targetDx + targetDy * targetDy)
        const targetNormalX = targetLen > 0 ? -targetDy / targetLen : 0
        const targetNormalY = targetLen > 0 ? targetDx / targetLen : 0

        // 更新基数标注位置
        relation.sourceCardinalityX = sourceMidX + sourceNormalX * textOffset
        relation.sourceCardinalityY = sourceMidY + sourceNormalY * textOffset
        relation.targetCardinalityX = targetMidX + targetNormalX * textOffset
        relation.targetCardinalityY = targetMidY + targetNormalY * textOffset
      }
    },

    // 检测椭圆与矩形的碰撞
    checkEllipseRectCollision(ellipseX, ellipseY, ellipseRx, ellipseRy, rectX, rectY, rectWidth, rectHeight) {
      // 找到矩形上距离椭圆中心最近的点
      const closestX = Math.max(rectX, Math.min(ellipseX, rectX + rectWidth))
      const closestY = Math.max(rectY, Math.min(ellipseY, rectY + rectHeight))
      
      // 计算该点到椭圆中心的距离（归一化到椭圆坐标系）
      const dx = (closestX - ellipseX) / ellipseRx
      const dy = (closestY - ellipseY) / ellipseRy
      const distance = Math.sqrt(dx * dx + dy * dy)
      
      // 如果距离小于1，说明该点在椭圆内部，发生碰撞
      return distance < 1
    },

    // 计算椭圆到矩形的最短距离
    getDistanceEllipseToRect(ellipseX, ellipseY, ellipseRx, ellipseRy, rectX, rectY, rectWidth, rectHeight) {
      // 找到矩形上距离椭圆中心最近的点
      const closestX = Math.max(rectX, Math.min(ellipseX, rectX + rectWidth))
      const closestY = Math.max(rectY, Math.min(ellipseY, rectY + rectHeight))
      
      // 计算该点到椭圆边缘的距离
      const dx = closestX - ellipseX
      const dy = closestY - ellipseY
      
      // 计算椭圆上该方向的点
      const angle = Math.atan2(dy, dx)
      const ellipseEdgeX = ellipseX + ellipseRx * Math.cos(angle)
      const ellipseEdgeY = ellipseY + ellipseRy * Math.sin(angle)
      
      // 计算距离
      const distX = closestX - ellipseEdgeX
      const distY = closestY - ellipseEdgeY
      return Math.sqrt(distX * distX + distY * distY)
    },

    startResize(event, node) {
      this.resizingNode = node
      const svg = this.$el.querySelector('.diagram-svg')
      const pt = svg.createSVGPoint()
      pt.x = event.clientX
      pt.y = event.clientY
      const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
      
      this.resizeStartX = svgP.x
      this.resizeStartY = svgP.y
      
      if (node.width) {
        this.resizeStartWidth = node.width
        this.resizeStartHeight = node.height
      } else {
        this.resizeStartRx = node.rx
        this.resizeStartRy = node.ry
      }
    },

    openExportDialog() { this.isExportDialogVisible = true },

    handleExportConfirm(options) {
      if (options.format === 'drawio') {
        this.exportToDrawio()
      } else {
        this.exportToImage(options)
      }
    },

    exportToDrawio() {
      // 生成 draw.io XML 格式
      let xml = '<?xml version="1.0" encoding="UTF-8"?>\n'
      xml += '<mxfile host="app.diagrams.net" modified="' + new Date().toISOString() + '" agent="ER Diagram Generator" version="21.0.0" type="device">\n'
      xml += '  <diagram name="ER Diagram" id="er-diagram">\n'
      xml += '    <mxGraphModel dx="1422" dy="794" grid="1" gridSize="10" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="3000" pageHeight="3000" math="0" shadow="0">\n'
      xml += '      <root>\n'
      xml += '        <mxCell id="0" />\n'
      xml += '        <mxCell id="1" parent="0" />\n'

      let cellId = 2

      // 导出实体节点
      this.entities.forEach(entity => {
        if (!entity.visible) return
        xml += `        <mxCell id="${cellId}" value="${this.escapeXml(entity.name)}" style="rounded=0;whiteSpace=wrap;html=1;strokeColor=#000000;fillColor=#ffffff;fontFamily=宋体;fontSize=22;" vertex="1" parent="1">\n`
        xml += `          <mxGeometry x="${entity.x}" y="${entity.y}" width="${entity.width}" height="${entity.height}" as="geometry" />\n`
        xml += '        </mxCell>\n'
        entity.drawioId = cellId
        cellId++
      })

      // 导出属性节点
      this.attributes.forEach(attr => {
        if (!attr.visible) return
        const x = attr.x - attr.rx
        const y = attr.y - attr.ry
        const width = attr.rx * 2
        const height = attr.ry * 2
        // 主键添加下划线样式 - draw.io 使用 fontStyle=4 表示下划线
        const fontStyle = attr.isPrimaryKey ? 'fontStyle=4;' : ''
        xml += `        <mxCell id="${cellId}" value="${this.escapeXml(attr.name)}" style="ellipse;whiteSpace=wrap;html=1;strokeColor=#000000;fillColor=#ffffff;fontFamily=宋体;fontSize=20;${fontStyle}" vertex="1" parent="1">\n`
        xml += `          <mxGeometry x="${x}" y="${y}" width="${width}" height="${height}" as="geometry" />\n`
        xml += '        </mxCell>\n'
        attr.drawioId = cellId
        cellId++
      })

      // 导出属性到实体的连接线
      this.links.forEach(link => {
        const attrKey = link.id.replace('link-', '')
        const attr = this.attributes.find(a => a.key === attrKey)
        if (!attr || !attr.visible) return
        
        const entity = this.entities.find(e => e.key === attr.entityKey)
        if (!entity || !entity.visible) return

        xml += `        <mxCell id="${cellId}" value="" style="endArrow=none;html=1;strokeColor=#000000;" edge="1" parent="1" source="${entity.drawioId}" target="${attr.drawioId}">\n`
        xml += '          <mxGeometry relative="1" as="geometry" />\n'
        xml += '        </mxCell>\n'
        cellId++
      })

      // 导出外键关系（菱形、连接线、基数标注）
      this.foreignKeyRelations.forEach(relation => {
        if (!relation.visible) return
        
        const sourceEntity = this.entities.find(e => e.key === relation.sourceEntityKey)
        const targetEntity = this.entities.find(e => e.key === relation.targetEntityKey)
        
        if (!sourceEntity || !targetEntity || !sourceEntity.visible || !targetEntity.visible) return

        // 1. 创建菱形节点（关系）
        const diamondX = relation.diamondX - relation.diamondWidth
        const diamondY = relation.diamondY - relation.diamondHeight
        const diamondWidth = relation.diamondWidth * 2
        const diamondHeight = relation.diamondHeight * 2
        
        xml += `        <mxCell id="${cellId}" value="${this.escapeXml(relation.name)}" style="rhombus;whiteSpace=wrap;html=1;strokeColor=#000000;fillColor=#ffffff;fontFamily=宋体;fontSize=18;" vertex="1" parent="1">\n`
        xml += `          <mxGeometry x="${diamondX}" y="${diamondY}" width="${diamondWidth}" height="${diamondHeight}" as="geometry" />\n`
        xml += '        </mxCell>\n'
        const diamondId = cellId
        cellId++

        // 2. 创建源实体到菱形的连接线（带源端基数标注）
        xml += `        <mxCell id="${cellId}" value="${this.escapeXml(relation.sourceCardinality)}" style="endArrow=none;html=1;strokeColor=#000000;fontFamily=Arial;fontSize=18;" edge="1" parent="1" source="${sourceEntity.drawioId}" target="${diamondId}">\n`
        xml += '          <mxGeometry relative="1" as="geometry" />\n'
        xml += '        </mxCell>\n'
        cellId++

        // 3. 创建菱形到目标实体的连接线（带目标端基数标注）
        xml += `        <mxCell id="${cellId}" value="${this.escapeXml(relation.targetCardinality)}" style="endArrow=none;html=1;strokeColor=#000000;fontFamily=Arial;fontSize=18;" edge="1" parent="1" source="${diamondId}" target="${targetEntity.drawioId}">\n`
        xml += '          <mxGeometry relative="1" as="geometry" />\n'
        xml += '        </mxCell>\n'
        cellId++
      })

      xml += '      </root>\n'
      xml += '    </mxGraphModel>\n'
      xml += '  </diagram>\n'
      xml += '</mxfile>'

      // 下载文件
      const blob = new Blob([xml], { type: 'application/xml' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const now = new Date()
      const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`
      a.download = `${ts}ER图.drawio`
      a.href = url
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      this.$message.success('导出成功！可以使用 Draw.io 打开编辑')
    },

    escapeXml(text) {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
    },

    exportToImage(options) {
      const svgElement = this.$el.querySelector('.diagram-svg')
      if (!svgElement) return

      // 克隆 SVG 以避免修改原始元素
      const clonedSvg = svgElement.cloneNode(true)
      
      // 计算实际内容的边界
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
      
      this.entities.forEach(entity => {
        if (!entity.visible) return
        minX = Math.min(minX, entity.x)
        minY = Math.min(minY, entity.y)
        maxX = Math.max(maxX, entity.x + entity.width)
        maxY = Math.max(maxY, entity.y + entity.height)
      })
      
      this.attributes.forEach(attr => {
        if (!attr.visible) return
        minX = Math.min(minX, attr.x - attr.rx)
        minY = Math.min(minY, attr.y - attr.ry)
        maxX = Math.max(maxX, attr.x + attr.rx)
        maxY = Math.max(maxY, attr.y + attr.ry)
      })
      
      // 添加边距
      const padding = 50
      minX -= padding
      minY -= padding
      maxX += padding
      maxY += padding
      
      const contentWidth = maxX - minX
      const contentHeight = maxY - minY
      
      // 设置 viewBox 为实际内容区域
      clonedSvg.setAttribute('viewBox', `${minX} ${minY} ${contentWidth} ${contentHeight}`)
      clonedSvg.setAttribute('width', contentWidth)
      clonedSvg.setAttribute('height', contentHeight)
      
      const svgData = new XMLSerializer().serializeToString(clonedSvg)
      
      if (options.format === 'svg') {
        const blob = new Blob([svgData], { type: 'image/svg+xml' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        const now = new Date()
        const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`
        a.download = `${ts}ER图.svg`
        a.href = url
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功！')
        return
      }

      // PNG/JPEG 导出
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      // 使用 2倍或更高的分辨率以提高清晰度
      const scale = 3 // 3倍分辨率
      
      img.onload = () => {
        canvas.width = contentWidth * scale
        canvas.height = contentHeight * scale
        
        // 设置高质量渲染
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        if (options.background === 'white') {
          ctx.fillStyle = 'white'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
        }
        
        // 缩放上下文以匹配高分辨率
        ctx.scale(scale, scale)
        ctx.drawImage(img, 0, 0, contentWidth, contentHeight)
        
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          const now = new Date()
          const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`
          a.download = `${ts}ER图.${options.format}`
          a.href = url
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
          this.$message.success('导出成功！')
        }, `image/${options.format}`, 0.95) // 设置质量为 95%
      }
      
      img.onerror = (e) => {
        console.error('图片加载失败:', e)
        this.$message.error('导出失败，请重试')
      }
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
    },

    toggleFullscreen() {
      if (!document.fullscreenElement) {
        this.$refs.diagramContainer.requestFullscreen()
        this.isFullscreen = true
      } else {
        document.exitFullscreen()
        this.isFullscreen = false
      }
    },

    openEntitySelectDialog() {
      this.availableEntities = this.entities.map(e => ({ key: e.key, name: e.name }))
      this.selectedEntities = this.entities.filter(e => e.visible).map(e => e.key)
      this.isEntitySelectVisible = true
    },

    selectAllEntities() {
      this.selectedEntities = [...this.availableEntities.map(e => e.key)]
    },

    invertSelection() {
      const all = this.availableEntities.map(e => e.key)
      this.selectedEntities = all.filter(k => !this.selectedEntities.includes(k))
    },

    applyEntityFilter() {
      // 使用 visible 属性控制显示/隐藏，而不是删除
      this.entities.forEach(entity => {
        entity.visible = this.selectedEntities.includes(entity.key)
      })
      
      this.attributes.forEach(attr => {
        attr.visible = this.selectedEntities.includes(attr.entityKey)
      })
      
      // 隐藏连接到不可见实体的外键关系
      this.foreignKeyRelations.forEach(relation => {
        const sourceVisible = this.selectedEntities.includes(relation.sourceEntityKey)
        const targetVisible = this.selectedEntities.includes(relation.targetEntityKey)
        // 只有当源实体和目标实体都可见时，关系才可见
        relation.visible = sourceVisible && targetVisible
      })
      
      this.isEntitySelectVisible = false
      this.$message.success(`已筛选 ${this.selectedEntities.length} 个实体`)
    }
  }
}
</script>

<style scoped>
.diagram-output {
  background: #FFFFFF;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 空状态样式 */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: #fafafa;
}

.empty-state svg {
  color: #d1d5db;
  margin-bottom: 20px;
}

.empty-title {
  font-size: 14px;
  font-weight: normal;
  margin: 0 0 8px 0;
}

.empty-description {
  font-size: 14px;
  font-weight: normal;
  margin: 0;
  text-align: center;
  line-height: 1.6;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 24px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  min-height: 50px;
}

.toolbar-left { display: flex; gap: 10px; align-items: center; }
.toolbar-right { display: flex; gap: 10px; }

.canvas-hint-toolbar {
  font-size: 14px;
  color: #856404;
  background: #fff3cd;
  padding: 6px 14px;
  border-radius: 4px;
  white-space: nowrap;
}

.tool-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 14px;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.tool-btn i { font-size: 15px; }

.btn-purple { background: #8c6ff7; }
.btn-purple:hover { background: #7559d4; }
.btn-red { background: #ff6b6b; }
.btn-red:hover { background: #e55c5c; }
.btn-orange { background: #f59e0b; }
.btn-orange:hover { background: #d97706; }
.btn-blue { background: #409eff; }
.btn-blue:hover { background: #2b88e6; }

.diagram-wrapper {
  flex: 1;
  background-color: #F2F2F7;
  position: relative;
  overflow: hidden;
}

.diagram-container {
  width: 100%;
  height: calc(100vh - 170px);
  background-color: #FFFFFF;
  position: relative;
  border: 1px solid #E4E7ED;
  overflow: hidden;
}

.diagram-svg {
  width: 100%;
  height: 100%;
  cursor: grab;
  user-select: none;
}

.diagram-svg.panning {
  cursor: grabbing;
}

.draggable-node {
  cursor: move;
}

.draggable-node:hover {
  filter: brightness(0.95);
}

.resize-handle {
  cursor: nwse-resize;
}

.resize-handle:hover {
  r: 8;
}

.relationship-diamond {
  cursor: move;
}

.relationship-diamond:hover {
  fill: #f0f9ff;
}

.editable-text {
  cursor: pointer;
  font-weight: 300 !important;
}

.editable-text:hover {
  fill: #409eff;
}

.entity-select-header {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 12px;
}

.entity-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.dialog-footer { display: flex; justify-content: flex-end; gap: 8px; }
</style>
